import Vue from 'vue';
import moment from 'moment';
import ApiService from '@/service/apiService';
import Api from '@/config/api';

export default {
  state: {
    aiMode: false,
    aiLoading: false,
    aiMatchedSeekers: [],
    aiProfileModalOpen: false,
    aiProfileId: null,
    aiProfileIndex: null,
    aiProfileLoading: false,
    aiJobSeeker: null,
    aiApplicantJd: null,
    aiMatchedJds: [],
    aiJdView: 'list', // 'list' | 'detail'
    aiSelectedJd: null,
    aiJdActionLoading: false,
  },
  getters: {
    getAiMode(state) {
      return state.aiMode;
    },
    getAiLoading(state) {
      return state.aiLoading;
    },
    getAiMatchedSeekers(state) {
      return state.aiMatchedSeekers;
    },
    getAiProfileModalOpen(state) {
      return state.aiProfileModalOpen;
    },
    getAiProfileId(state) {
      return state.aiProfileId;
    },
    getAiProfileIndex(state) {
      return state.aiProfileIndex;
    },
    getAiProfileLoading(state) {
      return state.aiProfileLoading;
    },
    getAiJobSeeker(state) {
      return state.aiJobSeeker;
    },
    getAiApplicantJd(state) {
      return state.aiApplicantJd;
    },
    getAiMatchedJds(state) {
      return state.aiMatchedJds;
    },
    getAiJdView(state) {
      return state.aiJdView;
    },
    getAiSelectedJd(state) {
      return state.aiSelectedJd;
    },
    getAiJdActionLoading(state) {
      return state.aiJdActionLoading;
    },
  },
  mutations: {
    setAiMode(state, value) {
      state.aiMode = value;
    },
    setAiLoading(state, value) {
      state.aiLoading = value;
    },
    setAiMatchedSeekers(state, data) {
      state.aiMatchedSeekers = data || [];
    },
    setAiProfileModalOpen(state, { open, id, index }) {
      state.aiProfileModalOpen = open;
      state.aiProfileId = id === undefined ? null : id;
      state.aiProfileIndex = index === undefined ? null : index;
    },
    setAiProfileLoading(state, value) {
      state.aiProfileLoading = value;
    },
    setAiProfileData(state, { jobSeeker, applicantJd, aiMatchedJds }) {
      state.aiJobSeeker = jobSeeker;
      state.aiApplicantJd = applicantJd;
      state.aiMatchedJds = aiMatchedJds || [];
    },
    setAiJdView(state, view) {
      state.aiJdView = view;
    },
    setAiSelectedJd(state, jd) {
      state.aiSelectedJd = jd;
    },
    removeAiMatchedJd(state, pairId) {
      state.aiMatchedJds = state.aiMatchedJds.filter((jd) => jd.pair_id !== pairId);
    },
    setAiJdActionLoading(state, value) {
      state.aiJdActionLoading = value;
    },
    // Memo/status/bookmark are edited via the same shared components from the AI Matching
    // list AND the AI Matching profile panel, but both read from this separate aiMatchedSeekers/
    // aiJobSeeker state rather than applicant.js's `applicants` array — so a save from one side
    // never showed up on the other until a full reload. A store subscriber (store.js) mirrors
    // the applicant.js field-level mutations into this state via this single mutation instead
    // of touching every call site (StatusDropDown, Memo.vue, ApplicantRow.vue, Profile.vue, ...).
    syncAiMatchedSeekerFields(state, { id, fields }) {
      const index = state.aiMatchedSeekers.findIndex(a => a.id === id);
      if (index !== -1) {
        Vue.set(state.aiMatchedSeekers, index, {
          ...state.aiMatchedSeekers[index],
          ...fields,
        });
      }
      if (state.aiJobSeeker && state.aiJobSeeker.id === id) {
        state.aiJobSeeker = { ...state.aiJobSeeker, ...fields };
      }
    },
    resetAiProfileState(state) {
      state.aiProfileModalOpen = false;
      state.aiProfileId = null;
      state.aiProfileIndex = null;
      state.aiJobSeeker = null;
      state.aiApplicantJd = null;
      state.aiMatchedJds = [];
      state.aiJdView = 'list';
      state.aiSelectedJd = null;
    },
    resetAiMatchingState(state) {
      state.aiMode = false;
      state.aiMatchedSeekers = [];
      state.aiProfileModalOpen = false;
      state.aiProfileId = null;
      state.aiProfileIndex = null;
      state.aiJobSeeker = null;
      state.aiApplicantJd = null;
      state.aiMatchedJds = [];
      state.aiJdView = 'list';
      state.aiSelectedJd = null;
    },
  },
  actions: {
    async loadAutoMatchedSeekers({ commit }) {
      commit('setAiLoading', true);
      try {
        const { data } = await ApiService.get(Api.endpoints.AUTO_MATCHED_SEEKERS);
        commit('setAiMatchedSeekers', data.data);
      } catch (error) {
        console.log(error);
      } finally {
        commit('setAiLoading', false);
      }
    },
    async loadProfileWithAiJds({ commit }, jobseekerId) {
      commit('setAiProfileLoading', true);
      try {
        const { data } = await ApiService.get(Api.endpoints.PROFILE_WITH_AI_JDS(jobseekerId));
        commit('setAiProfileData', data.data);
      } catch (error) {
        console.log(error);
      } finally {
        commit('setAiProfileLoading', false);
      }
    },
    async updateAiMatchStatus({ commit, rootState }, { jobseekerId, jd, pairStatus }) {
      commit('setAiJdActionLoading', true);
      try {
        await ApiService.put(
          Api.endpoints.UPDATE_AI_MATCH_PAIR_STATUS(jobseekerId, jd._id, jd.pair_id),
          { pair_status: pairStatus },
        );

        if (pairStatus === 'matched') {
          // Same controller used to assign a JD to a jobseeker from the normal profile flow —
          // it auto-appends a memo entry (job title/URL/timestamp) server-side and returns the
          // updated jobseeker, which is what the top-left memo icon/modal render from.
          const { data } = await ApiService.put(Api.endpoints.MARK_JOBSEEKERS_MANUALLY(jd._id), {
            jobseekerId,
            timestamp: moment().format('YYYY-MM-DD HH:mm'),
          });
          commit('syncAiMatchedSeekerFields', { id: jobseekerId, fields: { memo: data.data.memo } });
          // Profile.vue (the top-left panel, mounted for both normal and AI profiles) reads
          // from applicant.profile, not aiJobSeeker — must be updated too or its Memo icon
          // never sees the new memo (setProfile replaces wholesale, so merge onto current).
          if (rootState.applicant.profile && rootState.applicant.profile.id === jobseekerId) {
            commit('setProfile', { ...rootState.applicant.profile, memo: data.data.memo });
          }
        }

        commit('removeAiMatchedJd', jd.pair_id);
        commit('setAiJdView', 'list');
        commit('setAiSelectedJd', null);
      } catch (error) {
        console.log(error);
        throw error;
      } finally {
        commit('setAiJdActionLoading', false);
      }
    },
  },
};
